import * as types from '@/store/mutation-types';
import { guid } from '@/mixins/guid';
import _ from 'lodash';

const state = {
  currentSite: {},
  activeSiteType: null,
  s3EmptyFolderQueue: [],
  boilerplates: [],
};

const getters = {
  currentSite(state) {
    return state.currentSite;
  },
  activeSiteType(state) {
    return state.activeSiteType;
  },
  s3EmptyFolderQueue(state) {
    return state.s3EmptyFolderQueue;
  },
  boilerplates(state) {
    return state.boilerplates;
  },
};

const mutations = {
  [types.SET_BOILERPLATES](state, p) {
    state.boilerplates = p;
  },
  [types.REMOVE_BOILERPLATE](state, p) {
    state.boilerplates.splice(p.i, 1);
  },
  [types.SET_CURRENT_SITE](state, p) {
    this._vm.$log.debug('SET_CURRENT_SITE: ', p);
    state.currentSite = p;
    state.activeSiteType = p.type;
  },
  [types.SAVE_NEW_SITE](state, p) {
    p.websites.push(p.newSite);
  },
  [types.SET_ACTIVE_SITE_TYPE](state, p) {
    state.activeSiteType = p;
  },
  [types.SAVE_SITE_INFO](state, p) {
    // we only change the site alias and not
    // the actual site name. that won't change
    // only for UX satisfaction
    p.currentSite.alias = p.newName;
    p.currentSite.analytics = p.analytics;
  },
  [types.DELETE_SITE](state, p) {
    p.websites.splice(p.i, 1);
  },
  [types.PURCHASE_DOMAIN](state, p) {
    p.site.domains.push(p.domain);
  },
  [types.ADD_OWNED_DOMAIN](state, p) {
    this._vm.$log.debug('ADD_OWNED_DOMAIN: ', p);
    if (p.currentDomain) {
      p.currentDomain.records.push(p.newDomain);
    } else {
      p.site.domains.push(p.newDomain);
    }
  },
  [types.DELETE_DOMAIN](state, p) {
    p.site.domains.splice(p.index, 1);
  },
  [types.LATE_TRANSFER_DOMAIN](state, p) {
    p.domain.managed = p.update.managed;
    p.domain.hostedZoneId = p.update.hostedZoneId;
    p.domain.nameServers = p.update.nameServers;
    p.domain.records = p.update.records;
  },
  [types.ADD_DOMAIN_RECORD](state, p) {
    p.currentDomain.records.push(p.newRecord);
  },
  [types.REMOVE_DOMAIN_RECORD](state, p) {
    p.currentDomain.records.splice(p.index, 1);
  },
  [types.UPDATE_DOMAIN_RECORD](state, p) {
    const record = p.currentDomain.records[p.index];
    record.domain = p.newRecord.domain;
    record.ttl = p.newRecord.ttl;
    record.values = p.newRecord.values;
  },
  [types.UPDATE_DOMAIN](state, p) {
    _.extend(p.currentDomain, p.updates);
  },
  [types.ENABLE_AUTO_RENEW](state, p) {
    p.domain.autoRenew = p.autoRenew;
  },
  [types.UPDATE_SITE_STATUS](state, p) {
    const status = p.status === 'suspend' ? 'suspended' : 'live';
    p.site.status = status;
  },
  [types.ADD_SITE_SSL](state, p) {
    p.site.SSL.certs.push(p.cert);
  },
  [types.REMOVE_SITE_SSL](state, p) {
    p.site.SSL.certs.splice(p.i, 1);
  },
  [types.SET_CERT_ACTIVE](state, p) {
    p.cert.status = 'active';
    p.cert.outsideDomains = [];
  },
  [types.ADD_EMPTY_FOLDER_QUEUE](state, p) {
    if (_.isArray(p)) {
      state.s3EmptyFolderQueue = p;
    } else {
      state.s3EmptyFolderQueue.push(p);
    }
  },
};

const actions = {
  setCurrentSite: ({ commit }, payload) =>
    new Promise((resolve) => {
      commit(types.SET_CURRENT_SITE, payload);
      resolve();
    }),
  saveNewSite: ({ commit, rootState }, payload) => {
    payload.websites = rootState.Global.websites;
    return new Promise((resolve) => {
      commit(types.SAVE_NEW_SITE, payload);
      resolve();
    });
  },
  saveSiteInfo: ({ commit }, payload) =>
    new Promise((resolve) => {
      commit(types.SAVE_SITE_INFO, payload);
      resolve();
    }),
  deleteSite: ({ commit, rootState }, payload) => {
    payload.websites = rootState.Global.websites;
    const i = _.findIndex(payload.websites, o => o._id === payload.site._id);
    payload.i = i;
    return new Promise((resolve) => {
      commit(types.DELETE_SITE, payload);
      resolve();
    });
  },
  purchaseDomain: ({ commit }, payload) => {
    commit(types.PURCHASE_DOMAIN, payload);
  },
  addDomainRecord: ({ commit }, payload) => {
    commit(types.ADD_DOMAIN_RECORD, payload);
  },
  updateDomainRecord: ({ commit }, payload) => {
    commit(types.UPDATE_DOMAIN_RECORD, payload);
  },
  removeDomainRecord({ commit }, payload) {
    return new Promise((resolve) => {
      commit(types.REMOVE_DOMAIN_RECORD, payload);
      resolve();
    });
  },
  deleteDomain: ({ commit }, payload) => {
    commit(types.DELETE_DOMAIN, payload);
  },
  addOwnedDomain: ({ commit }, payload) => {
    commit(types.ADD_OWNED_DOMAIN, payload);
  },
  enableDomainAutoRenew: ({ commit }, payload) => {
    commit(types.ENABLE_AUTO_RENEW, payload);
  },
  updateDomain: ({ commit }, payload) => {
    commit(types.UPDATE_DOMAIN, payload);
  },
  lateTransferDomain({ commit }, payload) {
    return new Promise((resolve) => {
      commit(types.LATE_TRANSFER_DOMAIN, payload);
      resolve();
    });
  },
  updateSiteStatus: ({ commit, dispatch }, payload) => {
    commit(types.UPDATE_SITE_STATUS, payload);
    dispatch('addNotification', {
      id: guid.methods.guid(),
      type: 'success',
      message: `Your "${payload.site.alias}" site status has been successfully updated!`,
    });
  },
  setCertActive: ({ commit }, payload) => {
    commit(types.SET_CERT_ACTIVE, payload);
  },
  addSiteSSL: ({ commit, dispatch }, payload) => {
    commit(types.ADD_SITE_SSL, payload);
    dispatch('addNotification', {
      id: guid.methods.guid(),
      type: 'success',
      message: 'Your SSL certificate has been requested!',
    });
  },
  removeSiteSSL: ({ commit }, payload) => {
    const i = _.findIndex(payload.site.SSL.certs, o => o._id === payload._id);
    payload.i = i;
    commit(types.REMOVE_SITE_SSL, payload);
  },
  addEmptyFolderQueue: ({ commit }, payload) => {
    commit(types.ADD_EMPTY_FOLDER_QUEUE, payload);
  },
  setBoilerplates: ({ commit }, payload) => {
    commit(types.SET_BOILERPLATES, payload);
  },
  setActiveSiteType: ({ commit }, payload) => {
    commit(types.SET_ACTIVE_SITE_TYPE, payload);
  },
  removeBoilerplate: ({ commit, state }, payload) => {
    payload.i = _.findIndex(state.boilerplates, b => b._id === payload._id);
    commit(types.REMOVE_BOILERPLATE, payload);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/vuex.js